import { type ReactElement } from "react";
import { Link } from "react-router-dom";
import type { Broker, Strategy, StrategyType } from "@/types/strategy";

const BROKER_LABEL: Record<Broker, string> = { kite: "Zerodha", fyers: "Fyers", dhan: "Dhan" };

interface Props {
  strategy: Strategy;
}

export function StrategyCard({ strategy }: Readonly<Props>): ReactElement {
  const killOn = strategy.kill_switch;
  const truncatedId = truncateId(strategy.id);
  // No `rel` on purpose (2026-08-12): this is a SAME-ORIGIN tab of our own
  // app, and `noopener` would put it in a fresh browsing-context group —
  // which is precisely where the shared TradingView tab cannot be found by
  // name, so every cockpit tab would spawn its own chart tab. `_blank` still
  // gives each strategy its own tab; only the isolation is dropped.
  return (
    <Link
      to={`/strategies/${strategy.id}`}
      target="_blank"
      className="card flex flex-col gap-4 transition-colors hover:border-accent-border"
    >
      <div className="flex items-start justify-between gap-2">
        <h3>{strategy.name}</h3>
        <StatePill killOn={killOn} />
      </div>
      <TypeBadges strategy={strategy} />
      <div className="font-mono text-xs text-ink-muted">{truncatedId}</div>
      <div className="grid grid-cols-3 gap-3 border-t border-line pt-3 text-sm">
        <Stat label="Trades" value="-" />
        <Stat label="Win rate" value="-" />
        <Stat label="P&L 7d" value="-" />
      </div>
      <div className="flex items-center gap-1.5 text-xs text-ink-secondary">
        <i className="ti ti-clock text-sm" />
        Window {strategy.trading_window_start.slice(0, 5)} -{" "}
        {strategy.trading_window_end.slice(0, 5)} IST
      </div>
    </Link>
  );
}

function TypeBadges({ strategy }: Readonly<{ strategy: Strategy }>): ReactElement {
  const { icon, label } = typeBadge(strategy.strategy_type);
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="pill bg-surface-2 text-ink-secondary">
        <i className={`ti ${icon} text-xs`} />
        {label}
      </span>
      <span className="pill bg-surface-2 text-ink-secondary">
        <i className="ti ti-building-bank text-xs" />
        {BROKER_LABEL[strategy.broker]}
      </span>
      {strategy.execution_mode === "PAPER" && (
        <span className="pill bg-warning-bg text-warning" data-testid="paper-badge">
          <i className="ti ti-flask text-xs" />
          Paper
        </span>
      )}
    </div>
  );
}

function typeBadge(type: StrategyType): { icon: string; label: string } {
  switch (type) {
    case "supertrend":
      return { icon: "ti-chart-line", label: "Supertrend" };
    case "ut_bot_stc":
      return { icon: "ti-chart-candle", label: "UT Bot + STC" };
    case "bb_bottom_buy":
      return { icon: "ti-arrow-bounce", label: "BB Bottom-Buy" };
    case "positional_theta":
      return { icon: "ti-hourglass-high", label: "Theta Spreads" };
    case "failed_breakout":
      return { icon: "ti-arrows-diff", label: "Failed Breakout" };
    case "stpivot_nifty":
      return { icon: "ti-chart-candle", label: "STPIVOT NIFTY" };
    case "stpivot_sensex":
      return { icon: "ti-chart-candle", label: "STPIVOT SENSEX" };
    case "manual":
      return { icon: "ti-hand-click", label: "Manual trading" };
    default:
      return { icon: "ti-webhook", label: "Webhook" };
  }
}

function StatePill({ killOn }: Readonly<{ killOn: boolean }>): ReactElement {
  if (killOn) {
    return (
      <span className="pill bg-warning-bg text-warning" data-testid="kill-switch-badge">
        <i className="ti ti-player-pause text-sm" />
        Kill switch ON
      </span>
    );
  }
  return (
    <span className="pill bg-success-bg text-success" data-testid="kill-switch-badge">
      <i className="ti ti-circle-check text-sm" />
      Armed
    </span>
  );
}

function Stat({ label, value }: Readonly<{ label: string; value: string }>): ReactElement {
  return (
    <div>
      <div className="text-xs text-ink-secondary">{label}</div>
      <div className="mt-0.5 font-medium">{value}</div>
    </div>
  );
}

function truncateId(id: string): string {
  if (id.length <= 13) return id;
  return `${id.slice(0, 8)}...${id.slice(-4)}`;
}
